/**
 * Summarize react-dropzone's FileRejection shape into a normalized form
 * the UI can render with translated reason strings.
 *
 * react-dropzone error codes we care about:
 *   - "file-too-large"   → exceeded maxSize
 *   - "file-invalid-type" → not in accept map
 *   - "too-many-files"   → exceeded maxFiles for the drop
 *
 * Anything else (very rare in practice — `file-too-small`,
 * `file-not-found`) gets bucketed as "unknown" so we never silently drop
 * a failure.
 */

export type RejectionReason = "too-large" | "too-long" | "invalid-type" | "too-many" | "unknown";

export type FileRejectionSummary = {
  fileName: string;
  reasons: RejectionReason[];
};

const CODE_TO_REASON: Record<string, RejectionReason> = {
  "file-too-large": "too-large",
  "file-invalid-type": "invalid-type",
  "too-many-files": "too-many",
};

type DropRejectionInput = {
  file: { name: string };
  errors: readonly { code: string }[];
};

export const summarizeDropRejections = (
  rejections: readonly DropRejectionInput[]
): FileRejectionSummary[] => {
  return rejections.map((r) => {
    const reasons = new Set<RejectionReason>();
    for (const err of r.errors) {
      reasons.add(CODE_TO_REASON[err.code] ?? "unknown");
    }
    return {
      fileName: r.file.name,
      reasons: Array.from(reasons),
    };
  });
};

/**
 * Translation key for a rejection reason. Centralized so the UI and any
 * future caller (e.g. toast, server-validation echo) agree.
 */
export const reasonTranslationKey = (reason: RejectionReason): string =>
  `uploadForm.rejectReason_${reason.replaceAll("-", "_")}`;
